"use client";

import { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
interface EditPasswordTabProps {
  onPasswordChange: (passwords: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => Promise<void>;
  labels: Record<string, string>;
}
export default function EditPasswordTab({
  onPasswordChange,
  labels
}: EditPasswordTabProps) {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    setSuccess(false);
  };
  const togglePasswordVisibility = (field: "current" | "new" | "confirm") => {
    setShowPasswords(prev_0 => ({
      ...prev_0,
      [field]: !prev_0[field]
    }));
  };
  const getPasswordStrength = () => {
    const pw = formData.newPassword;
    if (!pw) return null;
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^a-zA-Z0-9]/.test(pw)) score++;
    if (score >= 4) return {
      label: labels.strong || "Strong",
      color: "text-green-500",
      width: "w-full"
    };
    if (score >= 2) return {
      label: labels.good || "Good",
      color: "text-yellow-500",
      width: "w-2/3"
    };
    return {
      label: labels.weak || "Weak",
      color: "text-red-500",
      width: "w-1/3"
    };
  };
  const handleSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      setError(labels.passwordMismatch || "Passwords do not match");
      return;
    }
    if (formData.newPassword.length < 6) {
      setError(labels.passwordTooShort || "Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await onPasswordChange(formData);
      setSuccess(true);
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : labels.passwordChangeError || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };
  const handleReset = () => {
    setFormData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    setError(null);
    setSuccess(false);
  };
  const passwordStrength = getPasswordStrength();
  return <form onSubmit={handleSubmit} className="space-y-6">
            {/* Success/Error Messages */}
            {success && <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-600">
                    {labels.passwordChanged || "Password changed successfully"}
                </div>}
            {error && <div className="rounded-lg bg-error/10 border border-error/20 p-3 text-sm text-error">
                    {error}
                </div>}

            {/* Current Password */}
            <div className="space-y-1.5">
                <label htmlFor="currentPassword" className="block text-sm font-medium text-foreground">
                    {labels.currentPassword || "Current Password"}
                </label>
                <div className="relative">
                    <input id="currentPassword" name="currentPassword" type={showPasswords.current ? "text" : "password"} value={formData.currentPassword} onChange={handleInputChange} disabled={isLoading} required className="w-full h-10 px-3 pr-10 input text-sm disabled:opacity-50" />
                    <button type="button" onClick={() => togglePasswordVisibility("current")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground cursor-pointer">
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            {/* New Password */}
            <div className="space-y-1.5">
                <label htmlFor="newPassword" className="block text-sm font-medium text-foreground">
                    {labels.newPassword || "New Password"}
                </label>
                <div className="relative">
                    <input id="newPassword" name="newPassword" type={showPasswords.new ? "text" : "password"} value={formData.newPassword} onChange={handleInputChange} disabled={isLoading} required className="w-full h-10 px-3 pr-10 input text-sm disabled:opacity-50" />
                    <button type="button" onClick={() => togglePasswordVisibility("new")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground cursor-pointer">
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
                <p className="text-xs text-muted-foreground">
                    {labels.passwordRequirements || "At least 6 characters"}
                </p>
                {passwordStrength && <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-1">
                            <div className={`h-1 rounded-full transition-all duration-300 ${passwordStrength.width} ${passwordStrength.color === "text-green-500" ? "bg-green-500" : passwordStrength.color === "text-yellow-500" ? "bg-yellow-500" : "bg-red-500"}`} />
                        </div>
                        <span className={`text-xs ${passwordStrength.color}`}>{passwordStrength.label}</span>
                    </div>}
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground">
                    {labels.confirmPassword || "Confirm Password"}
                </label>
                <div className="relative">
                    <input id="confirmPassword" name="confirmPassword" type={showPasswords.confirm ? "text" : "password"} value={formData.confirmPassword} onChange={handleInputChange} disabled={isLoading} required className="w-full h-10 px-3 pr-10 input text-sm disabled:opacity-50" />
                    <button type="button" onClick={() => togglePasswordVisibility("confirm")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground cursor-pointer">
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
                <button type="submit" disabled={isLoading} className="py-2 px-4 font-medium text-sm btn-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                    {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isLoading ? labels.changingPassword || "Changing..." : labels.changePassword || "Change Password"}
                </button>
                <button type="button" onClick={handleReset} disabled={isLoading} className="py-2 px-4 border border-border rounded-lg font-medium text-sm text-foreground hover:bg-muted transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    {labels.reset || "Reset"}
                </button>
            </div>
        </form>;
}